/**
 * The one place that knows how a local table maps to a server table.
 *
 * Both SyncService and the Node `/api/v1/sync` endpoint are driven by this
 * description rather than by per-entity branching, so adding an entity is a data
 * change here plus a matching entry in the server registry - not new code in the
 * sync loop.
 */
import { EntityName } from './sync.model';

export interface EntityDescriptor {
  /** Key used in queue rows, API payloads and the server registry. */
  readonly name: EntityName;
  /** Dexie table name. */
  readonly table: string;
  /** MySQL table this mirrors. */
  readonly serverTable: string;
  /**
   * Primary key column on the server. Not always `id`: school_teachers uses
   * teacher_id, serviceproviders_schools uses school_id, school_classrooms uses
   * classroom_id.
   */
  readonly serverKey: string;
  /**
   * Whether local changes may be pushed. Reference data the teacher only reads
   * (and server-derived roll-ups) is pull-only: queueing edits for it would
   * invite conflicts over data the client has no authority to change.
   */
  readonly pushable: boolean;
  /**
   * A parent that may itself still be unsynced. At push time the parent's newly
   * issued server id is substituted into `serverField` before the child is sent.
   * Without this an answer created offline would reference an attempt id that
   * does not exist yet.
   */
  readonly parent?: {
    readonly entity: EntityName;
    /** Field on this record holding the parent's uuid. */
    readonly uuidField: string;
    /** Field on this record that must carry the parent's server id. */
    readonly serverField: string;
  };
}

export const SYNC_REGISTRY: Readonly<Record<EntityName, EntityDescriptor>> = {
  students: {
    name: 'students',
    table: 'students',
    serverTable: 'bmc6_assessment_childs',
    serverKey: 'id',
    pushable: false,
  },
  classrooms: {
    name: 'classrooms',
    table: 'classrooms',
    serverTable: 'sam5_multi_assessment_classes',
    serverKey: 'id',
    pushable: false,
  },
  assessments: {
    name: 'assessments',
    table: 'assessments',
    serverTable: 'sam5_multi_assessments',
    serverKey: 'id',
    pushable: false,
  },
  studentAssignments: {
    name: 'studentAssignments',
    table: 'studentAssignments',
    serverTable: 'sam5_multi_assessment_assignments',
    serverKey: 'id',
    pushable: false,
  },
  // ── Assessment structure. Authored by admins, read-only on the client. ──
  assessmentSections: {
    name: 'assessmentSections',
    table: 'assessmentSections',
    serverTable: 'sam5_multi_assessment_sections',
    serverKey: 'id',
    pushable: false,
  },
  sectionItems: {
    name: 'sectionItems',
    table: 'sectionItems',
    serverTable: 'sam5_multi_section_items',
    serverKey: 'id',
    pushable: false,
  },
  questions: {
    name: 'questions',
    table: 'questions',
    serverTable: 'sam5_multi_questions',
    serverKey: 'id',
    pushable: false,
  },
  questionOptions: {
    name: 'questionOptions',
    table: 'questionOptions',
    serverTable: 'sam5_multi_question_options',
    serverKey: 'id',
    pushable: false,
  },
  assignmentIntervals: {
    name: 'assignmentIntervals',
    table: 'assignmentIntervals',
    serverTable: 'sam5_multi_assignment_intervals',
    serverKey: 'id',
    // Derived by updateAttemptProgress from the answers on every save.
    pushable: false,
  },

  assessmentAttempts: {
    name: 'assessmentAttempts',
    table: 'assessmentAttempts',
    serverTable: 'sam5_multi_assessment_attempts',
    serverKey: 'id',
    pushable: true,
  },
  assessmentResults: {
    name: 'assessmentResults',
    table: 'assessmentResults',
    serverTable: 'sam5_multi_assessment_responses',
    serverKey: 'id',
    pushable: true,
    parent: {
      entity: 'assessmentAttempts',
      uuidField: 'attempt_uuid',
      serverField: 'attempt_id',
    },
  },
  selectedOptions: {
    name: 'selectedOptions',
    table: 'selectedOptions',
    serverTable: 'sam5_response_selected_options',
    serverKey: 'id',
    /**
     * Writable, with a second writer on the other side.
     *
     * The server also produces these rows - replaceSelectedOptions derives them
     * from the answer's response_json on every save - so a push routinely meets
     * a row that already exists for the same (response_id, option_id).
     *
     * That is safe because the server declares those two columns as a natural
     * key: the client's create resolves to the existing row and adopts it
     * instead of duplicating. Push order does the rest - the parent answer goes
     * first, so the server derives its rows before these arrive to converge on
     * them.
     */
    pushable: true,
    parent: {
      entity: 'assessmentResults',
      uuidField: 'response_uuid',
      serverField: 'response_id',
    },
  },

  reports: {
    name: 'reports',
    table: 'reports',
    serverTable: 'sam5_multi_attempt_section_scores',
    serverKey: 'id',
    // Recomputed server-side from the answers on every save. Pushing a locally
    // derived roll-up would fight the backend for ownership of the same number.
    pushable: false,
  },
};

/** Entities whose local edits are sent to the server, in dependency order. */
/**
 * Push order is dependency order: a child sent before its parent has no server
 * id to attach to. Answers wait for their attempt, selections for their answer.
 */
export const PUSHABLE_ENTITIES: readonly EntityName[] = [
  'assessmentAttempts',
  'assessmentResults',
  'selectedOptions',
];

/** Entities refreshed by pulling server changes. */
/**
 * Pull order matters: a child arriving before its parent has nothing to attach
 * to, so structure is fetched top-down (assessment -> sections -> items ->
 * questions -> options) before the answers that reference it.
 */
export const PULLABLE_ENTITIES: readonly EntityName[] = [
  'classrooms',
  'students',
  'studentAssignments',
  'assessments',
  'assessmentSections',
  'sectionItems',
  'questions',
  'questionOptions',
  'assignmentIntervals',
  'assessmentAttempts',
  'assessmentResults',
  'selectedOptions',
  'reports',
];

export function describe(entity: EntityName): EntityDescriptor {
  return SYNC_REGISTRY[entity];
}
